// Personalización de tema en tiempo de ejecución (Configuración → Tema).
//
// El usuario final elige un color principal (barra lateral) y un color de
// acento (botones/resaltados); a partir de esos dos colores se derivan
// automáticamente las variantes claras/oscuras necesarias (mismo cálculo que
// usa el script de arranque en index.html, para que no haya parpadeo al
// cargar la página). Todo se guarda en localStorage — es una preferencia por
// navegador/usuario, no una configuración compartida de la empresa.
//
// tailwind.config.js define los colores "navy"/"accent" como
// rgb(var(--color-x) / <alpha-value>), así que basta con actualizar estas
// variables CSS para que TODO el sistema (sidebar, botones, tarjetas,
// badges) cambie de color al instante, sin recompilar nada.

export type Tema = { primario: string; acento: string };

export type PresetTema = { id: string; nombre: string; primario: string; acento: string };

export const TEMA_POR_DEFECTO: Tema = { primario: "#1e2938", acento: "#059669" };

export const PRESETS_TEMA: PresetTema[] = [
  { id: "esmeralda", nombre: "Esmeralda profesional", primario: "#1e2938", acento: "#059669" },
  { id: "indigo", nombre: "Índigo vibrante", primario: "#1e1b4b", acento: "#4f46e5" },
  { id: "azul", nombre: "Azul corporativo", primario: "#0c2340", acento: "#2563eb" },
  { id: "ambar", nombre: "Carbón y ámbar", primario: "#1c1917", acento: "#b45309" },
];

const CLAVE_LOCALSTORAGE = "sigepro_theme";

function hexARgb(hex: string): [number, number, number] {
  const limpio = hex.replace("#", "");
  const r = parseInt(limpio.substring(0, 2), 16);
  const g = parseInt(limpio.substring(2, 4), 16);
  const b = parseInt(limpio.substring(4, 6), 16);
  return [r, g, b];
}

// Mezcla un color hex hacia un objetivo (blanco o negro) en la proporción
// `cantidad` (0 = el color original, 1 = el objetivo) — así se derivan
// variantes claras/oscuras a partir de un solo color elegido por el usuario,
// en vez de pedirle que elija 5 tonos distintos a mano.
function mezclarHex(hex: string, objetivo: [number, number, number], cantidad: number): string {
  const [r, g, b] = hexARgb(hex);
  const [tr, tg, tb] = objetivo;
  const nr = Math.round(r + (tr - r) * cantidad);
  const ng = Math.round(g + (tg - g) * cantidad);
  const nb = Math.round(b + (tb - b) * cantidad);
  return `${nr} ${ng} ${nb}`;
}

function tripleteRgb(hex: string): string {
  const [r, g, b] = hexARgb(hex);
  return `${r} ${g} ${b}`;
}

/** Aplica el tema al documento actual (cambio inmediato, visible sin recargar). */
export function aplicarTema(tema: Tema) {
  const raiz = document.documentElement.style;
  raiz.setProperty("--color-navy", tripleteRgb(tema.primario));
  raiz.setProperty("--color-navy-light", mezclarHex(tema.primario, [255, 255, 255], 0.18));
  raiz.setProperty("--color-accent", tripleteRgb(tema.acento));
  raiz.setProperty("--color-accent-light", mezclarHex(tema.acento, [255, 255, 255], 0.88));
  raiz.setProperty("--color-accent-dark", mezclarHex(tema.acento, [0, 0, 0], 0.18));
}

export function guardarTema(tema: Tema) {
  localStorage.setItem(CLAVE_LOCALSTORAGE, JSON.stringify(tema));
}

export function cargarTema(): Tema | null {
  try {
    const crudo = localStorage.getItem(CLAVE_LOCALSTORAGE);
    if (!crudo) return null;
    const t = JSON.parse(crudo);
    if (typeof t?.primario === "string" && typeof t?.acento === "string") return t;
    return null;
  } catch {
    return null;
  }
}

export function restablecerTema() {
  localStorage.removeItem(CLAVE_LOCALSTORAGE);
  aplicarTema(TEMA_POR_DEFECTO);
}

/** Se llama una vez al arrancar la app — por si el usuario cambió el tema en
 * otra pestaña/sesión desde la última carga (el script inline de index.html
 * ya aplicó el tema guardado antes del primer render, así que normalmente
 * esto es un no-op; existe para mantener React y localStorage sincronizados). */
export function inicializarTema() {
  const guardado = cargarTema();
  aplicarTema(guardado || TEMA_POR_DEFECTO);
}
